/**
 * SupervisorAgentViewPreview — Iter-141 · Section 6.
 *
 * Read-only mirror of the mobile field-agent execution flow. A
 * Supervisor picks any active Service Event in their region and sees
 * exactly what the assigned agent sees on their phone — current step,
 * completed steps with timestamps, remaining steps, TKT- exceptions
 * logged so far, and photos submitted.
 *
 * The panel is intentionally read-only: no controls to advance or
 * modify steps. Wired to the same Service Event data the mobile app
 * reads (/api/service-events/admin — supervisors are Ops-tier).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Camera, CheckCircle2, Circle, Eye, RefreshCw, TicketCheck, Timer,
} from 'lucide-react';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString([], {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export function SupervisorAgentViewPreview({ user, testId = 'supervisor-agent-preview' }) {
  const [events, setEvents] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/service-events/admin`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        const active = (d.events || []).filter((e) =>
          ['scheduled', 'in_progress'].includes(e.status),
        );
        setEvents(active);
        if (active.length && !selectedId) setSelectedId(active[0].service_event_id);
      } else {
        setEvents([]);
      }
    } catch { setEvents([]); } finally { setBusy(false); }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => (events || []).find((e) => e.service_event_id === selectedId) || null,
    [events, selectedId],
  );

  // Derive step progress. `steps_completed` is the mobile-authored
  // list of step names that have been checked off; anything before the
  // agent's `current_step_index` (if present) also counts. We fall
  // back gracefully if the mobile app hasn't shipped these fields yet
  // by treating an in_progress event as "on step 1".
  const stepDetail = useMemo(() => {
    if (!selected) return null;
    const steps = selected.step_list || [];
    const completed = new Set((selected.steps_completed || []).map((s) =>
      typeof s === 'string' ? s : s?.step,
    ));
    const timestampBy = {};
    (selected.steps_completed || []).forEach((s) => {
      if (s && typeof s === 'object' && s.step) timestampBy[s.step] = s.at;
    });
    const idx = typeof selected.current_step_index === 'number'
      ? selected.current_step_index
      : (selected.status === 'in_progress' ? Math.max(0, completed.size) : -1);
    return steps.map((label, i) => ({
      label,
      done: completed.has(label) || i < idx,
      current: i === idx && selected.status === 'in_progress',
      at: timestampBy[label] || null,
    }));
  }, [selected]);

  return (
    <section
      className="rounded-xl border-2 p-5 bg-zinc-900/70"
      style={{ borderColor: 'rgba(201, 169, 98, 0.35)' }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]"
            style={{ color: GOLD }}>
          <Eye className="h-4 w-4" /> Agent View Preview
          <Badge className="ml-2 bg-zinc-800 text-zinc-200 text-[9px]" data-testid={`${testId}-readonly`}>
            READ-ONLY
          </Badge>
        </h2>
        <button
          type="button"
          onClick={load}
          className="p-1.5 rounded-full border border-zinc-600 text-zinc-300 hover:text-white"
          data-testid={`${testId}-refresh`}
          disabled={busy}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-[11px] text-zinc-400 mb-3">
        Mirror of the mobile field-agent flow for any active service event in your region. You cannot advance or modify steps here.
      </p>

      {events === null ? (
        <p className="text-xs text-zinc-500" data-testid={`${testId}-loading`}>Loading active service events…</p>
      ) : events.length === 0 ? (
        <p className="text-xs text-zinc-500 py-6 text-center" data-testid={`${testId}-empty`}>
          No scheduled or in-progress service events in your region right now.
        </p>
      ) : (
        <>
          <label className="text-[9px] uppercase tracking-widest text-zinc-500">Select an active service event</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full mt-1 h-9 px-2 rounded bg-zinc-950 border border-zinc-700 text-white text-xs"
            data-testid={`${testId}-picker`}
          >
            {events.map((e) => (
              <option key={e.service_event_id} value={e.service_event_id}>
                {e.number} · {e.service_type_label} · {e.property_name} · {e.status}
              </option>
            ))}
          </select>

          {selected && (
            <div className="mt-4 space-y-4" data-testid={`${testId}-detail`}>
              {/* Header row — mimics the mobile "Today" screen card */}
              <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 p-3 text-[11px] text-zinc-300 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#FFD60A]">{selected.number}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-white">{selected.service_type_label}</span>
                </div>
                <p><span className="text-zinc-500">Property:</span> {selected.property_name}</p>
                <p><span className="text-zinc-500">Scheduled:</span> {fmt(selected.scheduled_start)}</p>
                {selected.started_at && (
                  <p><span className="text-zinc-500">Started:</span> {fmt(selected.started_at)}</p>
                )}
                <p>
                  <span className="text-zinc-500">Status:</span>{' '}
                  <Badge className={
                    selected.status === 'in_progress'
                      ? 'bg-amber-500 text-black text-[9px]'
                      : 'bg-zinc-700 text-white text-[9px]'
                  }>
                    {(selected.status || '').replace('_', ' ').toUpperCase()}
                  </Badge>
                </p>
              </div>

              {/* Steps ladder */}
              <div>
                <h3 className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2">
                  Steps ({(stepDetail || []).filter((s) => s.done).length} of {(stepDetail || []).length} complete)
                </h3>
                <ol className="space-y-1.5" data-testid={`${testId}-steps`}>
                  {(stepDetail || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px]"
                        data-testid={`${testId}-step-${i}`}>
                      {s.done ? (
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-400 shrink-0" />
                      ) : s.current ? (
                        <Timer className="h-3.5 w-3.5 mt-0.5 text-amber-300 shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 mt-0.5 text-zinc-600 shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className={s.done ? 'text-zinc-300 line-through' : s.current ? 'text-amber-200 font-semibold' : 'text-zinc-400'}>
                          {s.label}
                        </p>
                        {s.at && (
                          <p className="text-[9px] text-zinc-500">{fmt(s.at)}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Exceptions / TKT- numbers logged so far */}
              <div>
                <h3 className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <TicketCheck className="h-3 w-3" /> Exceptions logged
                </h3>
                {(selected.exceptions || []).length === 0 ? (
                  <p className="text-[11px] text-zinc-500" data-testid={`${testId}-no-exceptions`}>
                    None so far.
                  </p>
                ) : (
                  <ul className="space-y-1" data-testid={`${testId}-exceptions`}>
                    {selected.exceptions.map((ex, i) => (
                      <li key={i} className="rounded border border-red-600/40 bg-red-950/30 p-2 text-[11px] text-red-200"
                          data-testid={`${testId}-exception-${i}`}>
                        {ex.ticket_number && (
                          <span className="font-mono font-bold text-red-100 mr-2">{ex.ticket_number}</span>
                        )}
                        {ex.unit_label && <span className="text-zinc-400 mr-2">Unit {ex.unit_label}</span>}
                        <span>{ex.reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Photos submitted */}
              <div>
                <h3 className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <Camera className="h-3 w-3" /> Photos submitted ({(selected.photos || []).length})
                </h3>
                {(selected.photos || []).length === 0 ? (
                  <p className="text-[11px] text-zinc-500" data-testid={`${testId}-no-photos`}>
                    None yet.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid={`${testId}-photos`}>
                    {selected.photos.map((p, i) => (
                      <a key={i} href={p} target="_blank" rel="noreferrer">
                        <img
                          src={p}
                          alt={`agent-photo-${i}`}
                          className="h-20 w-20 object-cover rounded border border-zinc-700"
                          data-testid={`${testId}-photo-${i}`}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default SupervisorAgentViewPreview;
